/**
 * Return Order Detail Modal
 *
 * A comprehensive modal component for displaying detailed information about a return order.
 * Features include:
 * - Loading states with skeleton UI
 * - Error handling
 * - Copy functionality for order ID
 * - Responsive design
 * - Enhanced UX with animations and hover effects
 */

import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Typography
} from "@mui/material"
import ReceiptIcon from "@mui/icons-material/Receipt"
import Stack from "src/shared/components/stack"
import {
  GetReturnOrderQuery,
  useBookFusionReturnShipmentMutation,
  useGetReturnOrderQuery
} from "src/graphql/quark/quark.type"
import { useMemo, useState } from "react"
import { toast } from "react-toastify"

// Import local components
import ReturnOrderHeader from "./components/ReturnOrderHeader"
import LoadingSkeleton from "./components/LoadingSkeleton"
import ReturnOrderSummary from "./components/ReturnOrderSummary"
import ReturnOrderItems from "./components/ReturnOrderItems"

// Import types
import { ReturnOrderDetailModalProps } from "./types"

/**
 * Main Return Order Detail Modal Component
 *
 * @param open - Whether the modal is open
 * @param id - The return order ID to fetch details for
 * @param onClose - Callback function to close the modal
 */
export default function ReturnOrderDetailModal({ open, id, onClose }: ReturnOrderDetailModalProps) {
  // State for copy functionality
  const [copied, setCopied] = useState(false)

  // Fetch return order data
  const { data, loading, error } = useGetReturnOrderQuery({
    variables: {
      getReturnOrderId: id ?? ""
    },
    skip: !id
  })

  const [bookReturnShipment, { loading: bookingReturnShipment }] = useBookFusionReturnShipmentMutation()

  const handleBookReturnShipment = async () => {
    if (!id || bookingReturnShipment) return
    try {
      const resp = await bookReturnShipment({ variables: { returnOrderId: id } })
      if (resp.data?.bookFusionReturnShipment?.id) toast.success("Return shipment booked successfully")
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to book return shipment")
    }
  }

  // Memoize the detail data
  const detail: GetReturnOrderQuery["getReturnOrder"] = useMemo(() => {
    return data?.getReturnOrder as GetReturnOrderQuery["getReturnOrder"]
  }, [data])

  /**
   * Handle copying the return order ID to clipboard
   */
  const handleCopyId = async () => {
    if (detail?.secondaryId) {
      try {
        await navigator.clipboard.writeText(String(detail.secondaryId))
        setCopied(true)
        // Reset copied state after 2 seconds
        setTimeout(() => setCopied(false), 2000)
      } catch (err) {
        console.error("Failed to copy:", err)
      }
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      scroll="paper"
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: "0 8px 32px rgba(0,0,0,0.12)"
        }
      }}
    >
      {/* Modal Header */}
      <DialogTitle sx={{ pr: 6, pb: 2 }}>
        <ReturnOrderHeader
          detail={detail}
          loading={loading}
          copied={copied}
          onCopyId={handleCopyId}
          onClose={onClose}
        />
      </DialogTitle>

      <Divider />

      {/* Modal Content */}
      <DialogContent dividers sx={{ p: 3 }}>
        {loading ? (
          // Loading State
          <LoadingSkeleton />
        ) : error ? (
          // Error State
          <Alert
            severity="error"
            sx={{
              alignItems: "flex-start",
              backgroundColor: (theme) => theme.palette.mode === "dark" 
                ? "rgba(211, 47, 47, 0.15)" 
                : "rgba(211, 47, 47, 0.08)",
              border: "2px solid",
              borderColor: "error.main",
              "& .MuiAlert-icon": {
                fontSize: 32,
                color: "error.dark",
                opacity: 1
              },
              "& .MuiAlert-message": {
                width: "100%"
              }
            }}
          >
            <Box>
              <Typography variant="subtitle1" gutterBottom color="error.dark">
                Error Loading Return Order
              </Typography>
              <Typography variant="body2" color="error.dark" sx={{ opacity: 0.95 }}>
                Unable to load the return order details. Please try again.
              </Typography>
            </Box>
          </Alert>
        ) : !detail ? (
          // Empty State
          <Box textAlign="center" py={4}>
            <ReceiptIcon sx={{ fontSize: 64, color: "grey.400", mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No Return Order Found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              The requested return order could not be found or may have been deleted.
            </Typography>
          </Box>
        ) : (
          // Success State - Display Return Order Details
          <Stack gap={3}>
            {/* Order Summary Section */}
            <ReturnOrderSummary detail={detail} />

            {/* Returned Items Section */}
            <ReturnOrderItems detail={detail} />

            {/* Return Comment Section (if exists) */}
            {detail.comment && (
              <Alert
                severity="info"
                sx={{
                  alignItems: "flex-start",
                  backgroundColor: (theme) => theme.palette.mode === "dark"
                    ? "rgba(2, 136, 209, 0.15)"
                    : "rgba(2, 136, 209, 0.08)",
                  border: "2px solid",
                  borderColor: "info.main",
                  "& .MuiAlert-icon": {
                    fontSize: 32,
                    color: "info.dark",
                    mt: 0.5,
                    opacity: 1
                  },
                  "& .MuiAlert-message": {
                    width: "100%"
                  }
                }}
              >
                <Box>
                  <Typography variant="subtitle1" gutterBottom color="info.dark">
                    Return Comment
                  </Typography>
                  <Typography variant="body2" color="info.dark" sx={{ lineHeight: 1.6, opacity: 0.95 }}>
                    {detail.comment}
                  </Typography>
                </Box>
              </Alert>
            )}
          </Stack>
        )}
      </DialogContent>

      <Divider />

      {/* Modal Actions */}
      <DialogActions sx={{ p: 2 }}>
        {detail ? (
          <Button
            onClick={handleBookReturnShipment}
            disabled={bookingReturnShipment}
            variant="outlined"
            sx={{ px: 3, py: 1, borderRadius: 2, fontWeight: 600, textTransform: "none", mr: "auto" }}
          >
            {bookingReturnShipment ? "Booking…" : "Book Return Shipment"}
          </Button>
        ) : null}
        <Button
          onClick={onClose}
          variant="contained"
          color="primary"
          sx={{
            px: 3,
            py: 1,
            borderRadius: 2,
            fontWeight: 600,
            textTransform: "none",
            "&:hover": {
              transform: "translateY(-1px)",
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)"
            },
            transition: "all 0.2s"
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  )
}
